import React, { Suspense, useEffect, useState } from 'react';

import { useResponsive } from '../../../context/ResponsiveContext';
import { CustomersWithCustomElementsE } from '../../../declarations/enums/common';
import { HeaderDataI } from '../../../declarations/interfaces/customer';
import { BurgerIcon, CloseIcon } from '../../Icon/Icon';
import {
  BurgerButtonStyled,
  HeaderStyled,
  LogoLinkStyled,
  LogoStyled,
  MobileNavStyled,
  NavStyled,
  OptionalSupSectionStyled,
} from './Header.styled';

type HeaderPropsT = {
  data: HeaderDataI;
  logoSrc: string;
  logoLink: string;
  slugUrl: string;
};

export type CustomHeaderPropsT = {
  logoLink?: string;
  logoSrc?: string;
};

const IlhaDeToqueHeader = React.lazy(
  () => import('../../CustomHeaders/IlhaDeToqueHeader/IlhaDeToqueHeader')
);
const SixSensesDouroValleyHeader = React.lazy(
  () => import('../../CustomHeaders/SixSensesDouroValleyHeader/SixSensesDouroValleyHeader')
);
const WayajEarthFriendlyTravelHeader = React.lazy(
  () => import('../../CustomHeaders/WayajEarthFriendlyTravelHeader/WayajEarthFriendlyTravelHeader')
);
const MuraltoMadridPrincesaHeader = React.lazy(
  () => import('../../CustomHeaders/MuraltoMadridPrincesaHeader/MuraltoMadridPrincesaHeader')
);
const BahiaMarBotiqueHeader = React.lazy(
  () => import('../../CustomHeaders/BahiaMarBotiqueHeader/BahiaMarBotiqueHeader')
);
const MondiLodgeHeader = React.lazy(
  () => import('../../CustomHeaders/MondiLodgeHeader/MondiLodgeHeader')
);
const TheMerchantHouseHeader = React.lazy(
  () => import('../../CustomHeaders/TheMerchantHouseHeader/TheMerchantHouseHeader')
);
const TamarindTreeDominicaHeader = React.lazy(
  () => import('../../CustomHeaders/TamaridTreeDominicaHeader/TamaridTreeDominicaHeader')
);
const InterAmericanInstituteHeader = React.lazy(
  () => import('../../CustomHeaders/InterAmericanInstituteHeader/InterAmericanInstituteHeader')
);

const CustomHeaders = {
  [CustomersWithCustomElementsE.ILHA_DE_TOQUE_ECO_BOTIQUE_HOTEL_SAO_SEBASTIAO]: {
    header: ({ logoSrc = '' }: CustomHeaderPropsT) => <IlhaDeToqueHeader logoUrl={logoSrc} />,
  },
  [CustomersWithCustomElementsE.SIX_SENSES_DOURO_VALLEY_SAMODAES]: {
    header: () => <SixSensesDouroValleyHeader />,
  },
  [CustomersWithCustomElementsE.WAYAJ_EARTH_FRIENDLY_TRAVEL]: {
    header: ({ logoSrc = '' }: CustomHeaderPropsT) => (
      <WayajEarthFriendlyTravelHeader logoUrl={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.MURAL_TO]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <MuraltoMadridPrincesaHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.BAHIA_MAR_BOTIQUE]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <BahiaMarBotiqueHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.MONDI_LODGE]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <MondiLodgeHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.THE_MERCHANT_HOUSE]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <TheMerchantHouseHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.TAMARIND_TREE_DOMINICA]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <TamarindTreeDominicaHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
  [CustomersWithCustomElementsE.INTER_AMERICAN_INSTITUTE_FOR_COOPERATION]: {
    header: ({ logoLink = '', logoSrc = '' }: CustomHeaderPropsT) => (
      <InterAmericanInstituteHeader logoLink={logoLink} logoSrc={logoSrc} />
    ),
  },
};

export const Header: React.FC<HeaderPropsT> = ({ data, logoSrc, logoLink, slugUrl }) => {
  const { optionalNavHtml, navbarData } = data;

  const [isMenuOpen, setMenuOpen] = useState(false);
  const { isDesktop } = useResponsive();

  const customHeader = CustomHeaders[slugUrl as keyof typeof CustomHeaders]?.header({
    logoLink,
    logoSrc,
  } as { logoLink: string; logoSrc: string });

  useEffect(() => {
    if (isDesktop && isMenuOpen) {
      setMenuOpen(false);
    }
  }, [isDesktop]);

  return (
    <>
      {!!customHeader ? (
        <Suspense fallback={null}>{customHeader}</Suspense>
      ) : (
        <>
          <OptionalSupSectionStyled dangerouslySetInnerHTML={{ __html: optionalNavHtml }} />
          <HeaderStyled as="header">
            <LogoLinkStyled href={logoLink}>
              <LogoStyled src={logoSrc} alt="logo" />
            </LogoLinkStyled>

            <NavStyled dangerouslySetInnerHTML={{ __html: navbarData }} />

            <BurgerButtonStyled type="button" onClick={() => setMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <CloseIcon /> : <BurgerIcon />}
            </BurgerButtonStyled>
          </HeaderStyled>

          {isMenuOpen && <MobileNavStyled dangerouslySetInnerHTML={{ __html: navbarData }} />}
        </>
      )}
    </>
  );
};

export default Header;
